const DEFAULT_MAX_BYTES = 1.8 * 1024 * 1024
const DEFAULT_MAX_WIDTH = 2500

function isHeic(file) {
  const name = (file.name || '').toLowerCase()
  const type = (file.type || '').toLowerCase()
  return (
    type.includes('heic') ||
    type.includes('heif') ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  )
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not read this image. Please use JPG or PNG.'))
    }
    img.src = url
  })
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality)
  })
}

/**
 * Shrink oversized images before upload so they stay under PHP upload_max_filesize.
 * Leaves small files and GIFs untouched. Rejects HEIC/HEIF (not accepted by the API).
 */
export async function compressImageFile(file, options = {}) {
  if (!file || !(file instanceof Blob)) return file

  const maxBytes = options.maxBytes || DEFAULT_MAX_BYTES
  const maxWidth = options.maxWidth || DEFAULT_MAX_WIDTH

  if (isHeic(file)) {
    throw new Error('HEIC/HEIF images are not supported. Please convert to JPG or PNG and try again.')
  }

  const type = (file.type || '').toLowerCase()
  if (type === 'image/gif') return file
  if (!type.startsWith('image/')) return file
  if (file.size <= maxBytes) return file

  const img = await loadImage(file)
  let width = img.width
  let height = img.height
  if (width > maxWidth) {
    height = Math.round((height * maxWidth) / width)
    width = maxWidth
  }

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  ctx.drawImage(img, 0, 0, width, height)

  const baseName = (file.name || 'image').replace(/\.[^.]+$/, '') || 'image'
  let quality = 0.85
  let blob = await canvasToBlob(canvas, 'image/jpeg', quality)

  while (blob && blob.size > maxBytes && quality > 0.45) {
    quality -= 0.1
    blob = await canvasToBlob(canvas, 'image/jpeg', quality)
  }

  if (!blob) return file

  // Still too large: scale down further
  let scale = 0.85
  while (blob.size > maxBytes && scale > 0.4) {
    const w = Math.max(1, Math.round(width * scale))
    const h = Math.max(1, Math.round(height * scale))
    canvas.width = w
    canvas.height = h
    ctx.drawImage(img, 0, 0, w, h)
    blob = await canvasToBlob(canvas, 'image/jpeg', 0.75)
    scale -= 0.1
  }

  if (!blob || blob.size > maxBytes) {
    throw new Error('Image is too large to upload. Please use a smaller JPG or PNG (under 2MB).')
  }

  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() })
}

/**
 * If FormData contains a `file` image, compress it in place and return a new FormData.
 */
export async function prepareImageFormData(formData) {
  if (!(formData instanceof FormData)) return formData

  const file = formData.get('file')
  if (!(file instanceof Blob)) return formData

  const compressed = await compressImageFile(file)
  if (compressed === file) return formData

  const next = new FormData()
  for (const [key, value] of formData.entries()) {
    if (key === 'file') {
      next.append('file', compressed, compressed.name || file.name || 'image.jpg')
    } else {
      next.append(key, value)
    }
  }
  return next
}
